import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Stack } from "@mui/material";

import SkipNextIcon from "@mui/icons-material/SkipNext";
import { FastForward } from "@mui/icons-material";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useSelector } from "react-redux";

import rookieIconSvg from "../../../assets/icons/coin-active-rookie.svg";
import intermediateIconSvg from "../../../assets/icons/coin-active-intermediate.svg";
import advancedIconSvg from "../../../assets/icons/coin-active-advance.svg";
import IconButton from "../IconButton/IconButton";

import useNarrationsNew from "../../../hooks/useNarrationsNew";
import "./ClueDetails.scss";

const LEVEL_KEYS = ["rookie", "intermediate", "advanced"];

const ClueDetails = ({
  selectedMissionId,
  selectedClueId,
  onClueChange,
  onComplete,
  autoPlay = true,
  showNavigation = true,
  setIsSpeaking,
  setSelectedMissionId,
  setSelectedClueId,
}) => {
  const missions = useSelector((state) => state.pinclue.missions);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayedClues, setDisplayedClues] = useState([]);

  const scrollRef = useRef(null);
  const isAdvancingRef = useRef(false);

  // --- Flatten pinned clues (include both real and decoy pinned clues) ---
  const allPinnedClues = useMemo(() => {
    const list = [];
    for (const mission of missions || []) {
      const missionNumber = String(mission.id || mission.missionId || "")
        .split("-")
        .pop();
      for (const lvl of LEVEL_KEYS) {
        const arr = mission?.coins?.[lvl];
        if (!Array.isArray(arr)) continue;
        for (const clue of arr) {
          if (clue?.pinned) {
            list.push({
              ...clue,
              missionId: mission.id,
              missionTitle: mission.missionTitle || mission.title || "",
              missionNumber: missionNumber || mission.missionNumber || "",
              level: (clue.level || lvl).toLowerCase(),
            });
          }
        }
      }
    }
    return list.sort((a, b) => {
      if (a.missionId !== b.missionId)
        return String(a.missionId).localeCompare(String(b.missionId));
      return (
        LEVEL_KEYS.indexOf(String(a.level).toLowerCase()) -
        LEVEL_KEYS.indexOf(String(b.level).toLowerCase())
      );
    });
  }, [missions]);

  // Fast lookup
  const indexByMissionClue = useMemo(() => {
    const m = new Map();
    allPinnedClues.forEach((c, i) => m.set(`${c.missionId}::${c.id}`, i));
    return m;
  }, [allPinnedClues]);

  const currentClue = allPinnedClues[currentIndex] || null;

  // Build audio key for current clue
  const buildAudioKey = useCallback((clue) => {
    if (!clue) return null;
    const level = (clue.level || "rookie").toLowerCase();
    const suffix = clue.clueType === "decoy" ? "D" : "R";
    return `mission_${clue.missionNumber}_${level}${suffix}`;
  }, []);

  const currentAudioKey = useMemo(
    () => buildAudioKey(currentClue),
    [currentClue, buildAudioKey]
  );

  // Refs for callbacks to avoid stale closures
  const currentIndexRef = useRef(currentIndex);
  const allPinnedCluesRef = useRef(allPinnedClues);
  const autoPlayRef = useRef(autoPlay);

  // Keep refs updated
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    allPinnedCluesRef.current = allPinnedClues;
  }, [allPinnedClues]);

  useEffect(() => {
    autoPlayRef.current = autoPlay;
  }, [autoPlay]);

  // useNarrationsNew hook for playing single sprite
  const {
    playSprite,
    stop: stopNarration,
    isPlaying,
  } = useNarrationsNew({
    storageKey: null, // Don't use storage for clue playback
    playOnce: false,
    onStart: () => {
      if (typeof setIsSpeaking === "function") setIsSpeaking(true);
    },
    onEnd: () => {
      if (typeof setIsSpeaking === "function") setIsSpeaking(false);

      // Auto-advance to next clue if autoPlay is enabled
      if (autoPlayRef.current && !isAdvancingRef.current) {
        isAdvancingRef.current = true;
        setTimeout(() => {
          const nextIdx = currentIndexRef.current + 1;
          if (nextIdx < allPinnedCluesRef.current.length) {
            const nextClue = allPinnedCluesRef.current[nextIdx];
            const curr = allPinnedCluesRef.current[currentIndexRef.current];

            setCurrentIndex(nextIdx);

            setDisplayedClues((prev) => {
              if (!curr) return [nextClue];
              if (nextClue.missionId === curr.missionId) {
                const exists = prev.some(
                  (c) =>
                    c.id === nextClue.id && c.missionId === nextClue.missionId
                );
                return exists ? prev : [...prev, nextClue];
              }
              return [nextClue];
            });

            onClueChange?.({
              missionId: nextClue.missionId,
              clueId: nextClue.id,
            });
          } else {
            onComplete?.();
          }
          isAdvancingRef.current = false;
        }, 120);
      }
    },
  });

  // Sync isSpeaking prop with isPlaying state
  useEffect(() => {
    if (typeof setIsSpeaking === "function") {
      setIsSpeaking(isPlaying);
    }
  }, [isPlaying, setIsSpeaking]);

  // When parent selects a mission+clue, jump there and build within-mission history
  useEffect(() => {
    if (!selectedMissionId || !selectedClueId) return;
    const idx = indexByMissionClue.get(
      `${selectedMissionId}::${selectedClueId}`
    );
    if (typeof idx === "number") {
      setCurrentIndex(idx);
      const target = allPinnedClues[idx];
      const sameMissionSlice = [];
      for (let i = idx; i >= 0; i--) {
        if (allPinnedClues[i].missionId !== target.missionId) break;
        sameMissionSlice.push(allPinnedClues[i]);
      }
      setDisplayedClues(sameMissionSlice.reverse());
    }
  }, [selectedMissionId, selectedClueId, indexByMissionClue, allPinnedClues]);

  // Initialize first pinned clue if none selected
  useEffect(() => {
    if (selectedMissionId || selectedClueId) return;
    if (allPinnedClues.length > 0 && displayedClues.length === 0) {
      setSelectedClueId(allPinnedClues[0].id);
      setSelectedMissionId(allPinnedClues[0].missionId);
      setCurrentIndex(0);
      setDisplayedClues([allPinnedClues[0]]);
    }
  }, [
    selectedMissionId,
    selectedClueId,
    allPinnedClues,
    displayedClues.length,
    setSelectedClueId,
    setSelectedMissionId,
  ]);

  // Clamp when data changes
  useEffect(() => {
    if (allPinnedClues.length === 0) {
      setCurrentIndex(0);
      setDisplayedClues([]);
      return;
    }

    if (currentIndex >= allPinnedClues.length) {
      const clamped = allPinnedClues.length - 1;
      setCurrentIndex(clamped);
      setDisplayedClues([allPinnedClues[clamped]]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPinnedClues.length, currentIndex]);

  // Navigate to next clue
  const handleNext = useCallback(() => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;
    stopNarration();

    setTimeout(() => {
      const nextIndex = currentIndex + 1;
      if (nextIndex >= allPinnedClues.length) {
        onComplete?.();
        isAdvancingRef.current = false;
        return;
      }

      const nextClue = allPinnedClues[nextIndex];
      const curr = allPinnedClues[currentIndex];

      setCurrentIndex(nextIndex);

      setDisplayedClues((prev) => {
        if (!curr) return [nextClue];
        if (nextClue.missionId === curr.missionId) {
          const exists = prev.some(
            (c) => c.id === nextClue.id && c.missionId === nextClue.missionId
          );
          return exists ? prev : [...prev, nextClue];
        }
        return [nextClue];
      });

      onClueChange?.({ missionId: nextClue.missionId, clueId: nextClue.id });

      setTimeout(() => {
        isAdvancingRef.current = false;
      }, 60);
    }, 8);
  }, [currentIndex, allPinnedClues, onComplete, onClueChange, stopNarration]);

  // Skip current audio and advance
  const skipCurrentAudio = useCallback(() => {
    stopNarration();

    if (currentIndex + 1 >= allPinnedClues.length) {
      onComplete?.();
      return;
    }
    setTimeout(() => handleNext(), 40);
  }, [
    currentIndex,
    allPinnedClues.length,
    stopNarration,
    onComplete,
    handleNext,
  ]);

  // Skip mission: jump to first pinned clue of next mission
  const skipMission = useCallback(() => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;
    stopNarration();

    const currMissionId = currentClue?.missionId;
    if (!currMissionId) {
      isAdvancingRef.current = false;
      return;
    }
    let nextIdx = -1;
    for (let i = currentIndex + 1; i < allPinnedClues.length; i++) {
      if (allPinnedClues[i].missionId !== currMissionId) {
        nextIdx = i;
        break;
      }
    }
    if (nextIdx === -1) {
      onComplete?.();
      isAdvancingRef.current = false;
      return;
    }
    const nextClue = allPinnedClues[nextIdx];
    setCurrentIndex(nextIdx);
    setDisplayedClues([nextClue]);
    onClueChange?.({ missionId: nextClue.missionId, clueId: nextClue.id });

    setTimeout(() => {
      isAdvancingRef.current = false;
    }, 60);
  }, [
    currentIndex,
    allPinnedClues,
    currentClue,
    onComplete,
    onClueChange,
    stopNarration,
  ]);

  // Skip all: jump to last pinned clue and complete
  const skipAll = useCallback(() => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true;
    stopNarration();

    if (allPinnedClues.length === 0) {
      onComplete?.();
      isAdvancingRef.current = false;
      return;
    }

    const lastIdx = allPinnedClues.length - 1;
    const lastClue = allPinnedClues[lastIdx];

    setCurrentIndex(lastIdx);
    setDisplayedClues([lastClue]);

    onClueChange?.({ missionId: lastClue.missionId, clueId: lastClue.id });
    onComplete?.();

    setTimeout(() => {
      isAdvancingRef.current = false;
    }, 60);
  }, [allPinnedClues, onComplete, onClueChange, stopNarration]);

  // Autoplay when currentClue changes
  useEffect(() => {
    if (currentClue && autoPlay && currentAudioKey) {
      const t = setTimeout(() => {
        playSprite(currentAudioKey);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [currentClue, autoPlay, currentAudioKey, playSprite]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopNarration();
    };
  }, [stopNarration]);

  const getLevelIconSrc = (level) => {
    switch (String(level).toLowerCase()) {
      case "rookie":
        return rookieIconSvg;
      case "intermediate":
        return intermediateIconSvg;
      case "advanced":
        return advancedIconSvg;
      default:
        return rookieIconSvg;
    }
  };

  // Levels to show in header (from displayed clues)
  const missionLevelsShown = useMemo(() => {
    const levels = new Set(
      displayedClues.map((c) => String(c.level).toLowerCase())
    );
    return LEVEL_KEYS.filter((l) => levels.has(l));
  }, [displayedClues]);

  if (allPinnedClues.length === 0) {
    return (
      <div className="clue-details-wrapper">
        <p>No pinned clues to review.</p>
      </div>
    );
  }
  if (!currentClue) {
    return (
      <div className="clue-details-wrapper">
        <p>No clue data available</p>
      </div>
    );
  }

  const getPosition = () => {
    if (
      selectedClueId === "clue-2" ||
      selectedClueId === "clue-5" ||
      selectedClueId === "clue-7" ||
      selectedClueId === "clue-8" ||
      selectedClueId === "clue-9" ||
      selectedClueId === "clue-10" ||
      selectedClueId === "clue-11" ||
      selectedClueId === "clue-24" ||
      selectedClueId === "clue-25" ||
      selectedClueId === "clue-26" ||
      selectedClueId === "clue-27" ||
      selectedClueId === "clue-28" ||
      selectedClueId === "clue-29" ||
      selectedClueId === "clue-30"
    ) {
      return "right";
    } else {
      return "left";
    }
  };

  return (
    <div className={`clue-details-wrapper pos-${getPosition()}`}>
      {showNavigation && (
        <Stack direction="row" spacing={2} justifyContent={"flex-end"}>
          <Stack direction="row" spacing={1}>
            <IconButton
              imageSrc={SkipNextIcon}
              onClick={skipCurrentAudio}
              title={"Skip"}
              disabled={!isPlaying}
            />
            <IconButton
              imageSrc={FastForward}
              onClick={skipMission}
              title={"Skip Mission"}
            />
            <IconButton
              imageSrc={LastPageIcon}
              onClick={skipAll}
              title={"Skip All"}
            />
          </Stack>
        </Stack>
      )}

      <div className="level-icons-row">
        {missionLevelsShown.map((lvl, idx) => (
          <img
            key={lvl}
            src={getLevelIconSrc(lvl)}
            alt={`${lvl} level`}
            className={`level-icon svg ${
              idx === missionLevelsShown.length - 1 ? "active" : ""
            }`}
          />
        ))}
      </div>

      <h2 className="clue-progress">
        Clues Mission {currentClue.missionNumber}: {currentClue.missionTitle}
      </h2>

      <Stack
        ref={scrollRef}
        spacing={3}
        sx={{ minHeight: 360, maxHeight: "45vh", overflowY: "auto", pr: 1 }}
      >
        {displayedClues.map((clue) => {
          const isCurrent = clue.id === currentClue?.id;
          const levelLower = String(clue.level || "rookie").toLowerCase();

          return (
            <div
              key={`${clue.missionId}::${clue.id}`}
              className={`clue-card ${isCurrent ? "clue-current" : ""}`}
              data-board-id={clue.boardElementId || clue.id}
              aria-live={isCurrent ? "polite" : "off"}
            >
              <p className="clue-section">
                {clue.clueType !== "decoy" && (
                  <strong className={`level-dot ${levelLower}`}>
                    {String(clue.level || "")
                      .charAt(0)
                      .toUpperCase() + String(clue.level || "").slice(1)}
                  </strong>
                )}
                {clue.title && (
                  <>
                    : <span className="clueName">{clue.title}</span>
                  </>
                )}
              </p>

              {/* Meaning (if present) */}
              {clue.meaning && (
                <div className="real-clue-section">
                  <span className="section-label">Meaning: </span>
                  <span className="section-content">{clue.meaning}</span>
                </div>
              )}

              {/* Decoy (show even if title missing) */}
              {clue.decoy && (
                <div
                  className="real-clue-section decoy-block"
                  role="region"
                  aria-label={clue.title || "Decoy clue"}
                >
                  <span className="section-label decoy-label">Decoy clue:</span>
                  <span className="section-content decoy-content">
                    {clue.decoy}
                  </span>
                </div>
              )}

              {/* Fallback */}
              {!clue.meaning && !clue.decoy && clue.description && (
                <div className="real-clue-section">
                  <span className="section-label">Description: </span>
                  <span className="section-content">{clue.description}</span>
                </div>
              )}
            </div>
          );
        })}
      </Stack>
    </div>
  );
};

export default ClueDetails;
